import React, { useState, useEffect, useCallback } from 'react';
import { RentalRegistration, texasComplianceService } from '../../services/texasComplianceService';
import { addressToText } from '../../utils/formatAddress';

export interface RentalRegistrationWidgetProps {
  onRegistrationClick?: (reg: RentalRegistration) => void;
  onPropertyClick?: (propertyId: string) => void;
  initialData?: RentalRegistration[];
}

export const RentalRegistrationWidget: React.FC<RentalRegistrationWidgetProps> = ({
  onRegistrationClick,
  onPropertyClick,
  initialData,
}) => {
  const [registrations, setRegistrations] = useState<RentalRegistration[]>(initialData || []);
  const [loading, setLoading] = useState<boolean>(!initialData);
  const [activeTab, setActiveTab] = useState<'ALL' | 'HOUSTON_STR' | 'DALLAS_SFR'>('ALL');
  const [error, setError] = useState<string | null>(null);

  const fetchRegistrations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await texasComplianceService.getRentalRegistrations();
      setRegistrations(data);
    } catch (err: any) {
      setError(err?.message || 'Failed to load rental registrations');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!initialData) {
      fetchRegistrations();
    }
  }, [fetchRegistrations, initialData]);

  const filtered = activeTab === 'ALL'
    ? registrations
    : registrations.filter((r) => r.jurisdictionType === activeTab);

  const getStatusBadge = (reg: RentalRegistration) => {
    if (reg.status === 'expired') {
      return (
        <span
          data-testid="reg-badge-expired"
          className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-200 uppercase"
        >
          {'Expired (' + Math.abs(reg.daysUntilRenewal) + 'd ago)'}
        </span>
      );
    }

    if (reg.status === 'expiring') {
      return (
        <span
          data-testid="reg-badge-expiring"
          className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200"
        >
          {'Expiring in ' + reg.daysUntilRenewal + 'd'}
        </span>
      );
    }

    return (
      <span
        data-testid="reg-badge-active"
        className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200"
      >
        {'Active (' + reg.daysUntilRenewal + 'd)'}
      </span>
    );
  };

  const getJurisdictionBadge = (type: 'HOUSTON_STR' | 'DALLAS_SFR') => {
    if (type === 'HOUSTON_STR') {
      return (
        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
          Houston STR
        </span>
      );
    }
    return (
      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-purple-50 text-purple-700 border border-purple-200">
        Dallas SFR
      </span>
    );
  };

  return (
    <div
      data-testid="rental-registration-widget"
      className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden p-4"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3 border-b border-gray-100 pb-2.5">
        <div className="flex items-center space-x-2.5">
          <div className="w-8 h-8 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-sm">
            <i className="fas fa-city"></i>
          </div>
          <div>
            <h3 className="font-bold text-gray-900 text-sm flex items-center gap-2">
              Municipal Rental Registrations
              <span className="text-[10px] font-medium px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded border border-blue-200">
                Houston & Dallas
              </span>
            </h3>
            <p className="text-[11px] text-gray-500">
              Houston STR ordinance & Dallas Ch. 27 annual renewals
            </p>
          </div>
        </div>

        {/* Tab switcher */}
        <div className="flex items-center bg-gray-100 p-0.5 rounded-lg text-[10px] font-semibold">
          <button
            onClick={() => setActiveTab('ALL')}
            className={'px-2 py-1 rounded-md transition ' + (activeTab === 'ALL' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900')}
          >
            All
          </button>
          <button
            onClick={() => setActiveTab('HOUSTON_STR')}
            className={'px-2 py-1 rounded-md transition ' + (activeTab === 'HOUSTON_STR' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900')}
          >
            Houston
          </button>
          <button
            onClick={() => setActiveTab('DALLAS_SFR')}
            className={'px-2 py-1 rounded-md transition ' + (activeTab === 'DALLAS_SFR' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-900')}
          >
            Dallas
          </button>
        </div>
      </div>

      {error && (
        <div className="p-2 mb-2 bg-red-50 border border-red-200 rounded text-xs text-red-600">
          {error}
        </div>
      )}

      {loading && registrations.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-xs">
          <i className="fas fa-spinner fa-spin mr-2"></i>Loading registrations...
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-8 text-center text-gray-400 text-xs">
          <i className="fas fa-building text-gray-300 text-lg mb-1 block"></i>
          No rental registrations found for this view.
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((reg) => (
            <div
              key={reg.id}
              data-testid={'registration-item-' + reg.id}
              onClick={() => onRegistrationClick && onRegistrationClick(reg)}
              className="p-3 bg-gray-50 hover:bg-gray-100/80 rounded-lg border border-gray-200/80 cursor-pointer transition-all flex items-center justify-between"
            >
              <div className="flex-1 min-w-0 pr-3">
                <div className="flex items-center space-x-2 mb-0.5">
                  <span className="font-bold text-gray-900 text-xs">
                    {reg.registrationNumber}
                  </span>
                  {getJurisdictionBadge(reg.jurisdictionType)}
                </div>

                <p
                  onClick={(e) => {
                    if (onPropertyClick) {
                      e.stopPropagation();
                      onPropertyClick(reg.propertyId);
                    }
                  }}
                  className="text-[11px] text-blue-600 hover:underline font-medium truncate"
                >
                  {reg.propertyName}
                </p>

                <p className="text-[10px] text-gray-400 truncate">
                  {addressToText(reg.address)}
                </p>
              </div>

              <div className="text-right flex-shrink-0">
                {getStatusBadge(reg)}
                <div className="text-[10px] text-gray-400 mt-1">
                  Renews: {reg.renewalDate}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer summary */}
      <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between text-[10px] text-gray-400">
        <span>Dallas Ch. 27 & Houston Municipal Code</span>
        <span>
          {registrations.filter((r) => r.status === 'expiring').length + ' expiring soon • ' +
           registrations.filter((r) => r.status === 'expired').length + ' expired'}
        </span>
      </div>
    </div>
  );
};

export default RentalRegistrationWidget;
